import { useState, useEffect, useRef, useCallback } from 'react';
import { useToast } from './use-toast';

/**
 * Custom hook to handle "Draft over Live" CMS functionality
 * Manages revision logic, auto-save, withdrawal, and status tracking
 */
export const useListingCMS = (listingId, listingType) => {
  const { toast } = useToast();
  const [currentListing, setCurrentListing] = useState(null);
  const [originalData, setOriginalData] = useState(null);
  const [autoSaveStatus, setAutoSaveStatus] = useState({ saving: false, lastSaved: null });
  const autoSaveTimerRef = useRef(null);

  /**
   * Fetch listing data and handle revision logic
   */
  const fetchListing = useCallback(async () => {
    if (!listingId) return null;

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = sessionStorage.getItem('token');
      
      const response = await fetch(`${BACKEND_URL}/api/listings/${listingId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch listing');
      }

      const data = await response.json();
      setCurrentListing(data);

      // Determine which data to load: revision_data (if editing) or main data
      let dataToLoad = { ...data };
      
      // Check if we should load from revision_data
      // This happens when: revision_status is 'revising' or 'revision_rejected'
      // OR when status is 'active' and revision_data exists (auto-saved changes)
      const shouldLoadRevision = (
        data.revision_status === 'revising' || 
        data.revision_status === 'revision_rejected' ||
        (data.status === 'active' && data.revision_data && Object.keys(data.revision_data).length > 0)
      );
      
      if (shouldLoadRevision && data.revision_data) {
        
        // Deep merge revision_data over the main data
        dataToLoad = {
          ...data,
          ...data.revision_data,
          // Preserve these fields from the main document
          id: data.id,
          status: data.status,
          revision_status: data.revision_status || 'revising',
          rejection_reason: data.rejection_reason,
          // Ensure nested 'data' object is merged correctly
          data: {
            ...(data.data || {}),
            ...(data.revision_data?.data || {})
          }
        };
        
        // Handle variants - prefer revision_data if present
        if (data.revision_data.variants !== undefined) {
          dataToLoad.variants = data.revision_data.variants;
        }
        
        // Handle variantCombinations - check both camelCase and snake_case
        const revisionCombinations = data.revision_data.variantCombinations || data.revision_data.variant_combinations;
        if (revisionCombinations !== undefined) {
          dataToLoad.variantCombinations = revisionCombinations;
        }
      }
      
      // Normalize snake_case to camelCase for variant_combinations
      if (dataToLoad.variant_combinations && !dataToLoad.variantCombinations) {
        dataToLoad.variantCombinations = dataToLoad.variant_combinations;
      }
      
      // Normalize field names: backend uses 'stock', frontend uses 'inventory'
      if (dataToLoad.data) {
        if (dataToLoad.data.stock !== undefined && dataToLoad.data.inventory === undefined) {
          dataToLoad.data.inventory = dataToLoad.data.stock;
        }
        // Also check for stock_quantity
        if (dataToLoad.data.stock_quantity !== undefined && dataToLoad.data.inventory === undefined) {
          dataToLoad.data.inventory = dataToLoad.data.stock_quantity;
        }
      }
      
      // Also normalize inventory in variantCombinations
      // Ensure both 'stock' and 'inventory' fields are set for compatibility
      if (dataToLoad.variantCombinations && Array.isArray(dataToLoad.variantCombinations)) {
        dataToLoad.variantCombinations = dataToLoad.variantCombinations.map(combo => {
          const stockValue = combo.stock ?? combo.inventory ?? 0;
          return {
            ...combo,
            stock: stockValue,
            inventory: stockValue
          };
        });
      }

      // Save original LIVE data for change detection (not revision data)
      setOriginalData({
        title: data.title,
        description: data.description,
        category: data.category,
        subcategory: data.subcategory,
        media: data.media,
        data: data.data
      });

      return dataToLoad;
    } catch (error) {
      console.error('[useListingCMS] Fetch error:', error);
      toast({
        title: 'Error',
        description: 'Failed to load listing',
        variant: 'destructive'
      });
      return null;
    }
  }, [listingId, toast]);

  /**
   * Get display status for the listing
   */
  const getDisplayStatus = useCallback(() => {
    if (!currentListing) return { label: 'Draft', variant: 'outline', description: '(Not submitted)' };

    const status = currentListing.status;
    const revisionStatus = currentListing.revision_status;

    if (revisionStatus === 'pending_revision_approval') {
      return { label: 'Active', variant: 'default', description: '(Pending Revision Review)', hasRevision: true };
    }
    if (revisionStatus === 'revision_rejected') {
      return { label: 'Revision Rejected', variant: 'destructive', description: '(Needs changes)', hasRevision: true };
    }
    if (revisionStatus === 'revising') {
      return { label: 'Active', variant: 'default', description: '(Editing - Auto-saved)', hasRevision: true };
    }

    // Standard statuses
    if (status === 'draft') {
      return { label: 'Draft', variant: 'outline', description: '(Not submitted)' };
    }
    if (status === 'pending_approval') {
      return { label: 'Pending Approval', variant: 'secondary', description: '(Awaiting admin approval)' };
    }
    if (status === 'active') {
      return { label: 'Active', variant: 'default', description: '(Live on marketplace)' };
    }
    if (status === 'inactive') {
      return { label: 'Inactive', variant: 'secondary', description: '(Hidden from marketplace)' };
    }
    if (status === 'rejected') {
      return { label: 'Rejected', variant: 'destructive', description: '(Needs revision)' };
    }

    return { label: status || 'Draft', variant: 'outline', description: '' };
  }, [currentListing]);

  /**
   * Detect if there are major (critical) changes
   */
  const hasCriticalChanges = useCallback((formData) => {
    if (!originalData || !currentListing?.status === 'active') return false;

    const criticalFields = ['title', 'category', 'subcategory', 'media'];
    
    for (const field of criticalFields) {
      if (JSON.stringify(formData[field]) !== JSON.stringify(originalData[field])) {
        return true;
      }
    }

    // Check price changes in data object
    if (formData.data?.price !== originalData.data?.price ||
        formData.data?.hourlyRate !== originalData.data?.hourlyRate ||
        formData.data?.dailyRate !== originalData.data?.dailyRate) {
      return true;
    }

    return false;
  }, [originalData, currentListing]);

  /**
   * Auto-save draft (debounced)
   * Uses POST /api/listings/{id}/auto-save endpoint
   */
  const triggerAutoSave = useCallback(async (formData) => {
    if (!listingId) {
      return;
    }

    // Clear existing timer
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }

    // Set new timer for debounce
    autoSaveTimerRef.current = setTimeout(async () => {
      try {
        setAutoSaveStatus({ saving: true, lastSaved: null });

        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
        const token = sessionStorage.getItem('token');

        // Use the correct auto-save endpoint (POST, not PUT)
        const response = await fetch(`${BACKEND_URL}/api/listings/${listingId}/auto-save`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(formData)
        });

        if (response.ok) {
          const result = await response.json();
          setAutoSaveStatus({ saving: false, lastSaved: new Date() });
        } else {
          const errorData = await response.json().catch(() => ({}));
          console.error('[useListingCMS] Auto-save failed:', response.status, errorData);
          throw new Error(errorData.detail || 'Auto-save failed');
        }
      } catch (error) {
        console.error('[useListingCMS] Auto-save error:', error);
        setAutoSaveStatus({ saving: false, lastSaved: null });
      }
    }, 2000); // 2 second debounce
  }, [listingId]);

  /**
   * Save listing as draft
   */
  const saveDraft = useCallback(async (formData) => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = sessionStorage.getItem('token');

      const method = listingId ? 'PUT' : 'POST';
      const url = listingId 
        ? `${BACKEND_URL}/api/listings/${listingId}` 
        : `${BACKEND_URL}/api/listings/`;

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const result = await response.json();
        toast({ title: 'Success', description: 'Draft saved successfully' });
        return { success: true, data: result };
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to save draft');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
      return { success: false, error: error.message };
    }
  }, [listingId, toast]);

  /**
   * Submit listing for approval
   */
  const submitForApproval = useCallback(async (formData) => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = sessionStorage.getItem('token');

      let listingIdToSubmit = listingId;

      // If new listing, create first
      if (!listingId) {
        const createResponse = await fetch(`${BACKEND_URL}/api/listings/`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(formData)
        });


        if (!createResponse.ok) {
          const error = await createResponse.json();
          console.error('[useListingCMS] Create failed:', error);
          throw new Error(error.detail || 'Failed to create listing');
        }

        const created = await createResponse.json();
        listingIdToSubmit = created.id;
      } else {
        // EXISTING LISTING: Save changes first (PUT) before submitting
        // This stores changes in revision_data for review
        const updateResponse = await fetch(`${BACKEND_URL}/api/listings/${listingId}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(formData)
        });


        if (!updateResponse.ok) {
          const error = await updateResponse.json();
          console.error('[useListingCMS] Update failed:', error);
          throw new Error(error.detail || 'Failed to save listing changes');
        }

        const updated = await updateResponse.json();
        listingIdToSubmit = listingId;
      }

      // Submit for approval
      const submitResponse = await fetch(`${BACKEND_URL}/api/listings/${listingIdToSubmit}/submit`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });


      const submitResult = await submitResponse.json();

      if (submitResult.success) {
        // Toast is shown by the calling component (UniversalSinglePageForm)
        return { success: true, data: submitResult, listingId: listingIdToSubmit, message: submitResult.message };
      } else if (submitResult.account_pending || submitResult.account_suspended || submitResult.account_rejected) {
        // Account status blocks publishing
        toast({
          title: 'Cannot Publish',
          description: submitResult.message || 'Your account is not eligible to publish listings.',
          variant: 'destructive'
        });
        return { success: false, accountBlocked: true, message: submitResult.message };
      } else if (submitResult.tier_limit_exceeded) {
        // Tier limit exceeded - return the data for modal display
        return { 
          success: false, 
          tierLimitExceeded: true,
          tierLimitData: {
            errorType: submitResult.error_type,
            message: submitResult.message,
            actionRequired: submitResult.action_required,
            options: submitResult.options || {},
            currentCount: submitResult.current_count,
            limit: submitResult.limit,
            currentTypes: submitResult.current_types || [],
            typesLimit: submitResult.types_limit
          }
        };
      } else {
        // Validation failed
        if (submitResult.validation_errors) {
          const errorMessages = submitResult.validation_errors
            .map(err => `${err.field}: ${err.message}`)
            .join('\n');
          toast({ 
            title: 'Validation Failed', 
            description: errorMessages,
            variant: 'destructive' 
          });
        }
        return { success: false, validationErrors: submitResult.validation_errors };
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
      return { success: false, error: error.message };
    }
  }, [listingId, toast]);

  /**
   * Withdraw submission
   */
  const withdrawSubmission = useCallback(async () => {
    if (!listingId) return { success: false };

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = sessionStorage.getItem('token');

      const response = await fetch(`${BACKEND_URL}/api/listings/${listingId}/withdraw`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();

      if (result.success) {
        toast({ 
          title: 'Success', 
          description: 'Submission withdrawn successfully' 
        });
        return { success: true };
      } else {
        throw new Error(result.error || 'Failed to withdraw submission');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
      return { success: false, error: error.message };
    }
  }, [listingId, toast]);

  /**
   * Check if withdrawal is allowed
   */
  const canWithdraw = useCallback(() => {
    if (!currentListing) return false;

    const status = currentListing.status;
    const revisionStatus = currentListing.revision_status;
    const adminViewedAt = currentListing.admin_viewed_at;

    // Can't withdraw if admin has already viewed it
    if (adminViewedAt) return false;

    // Can withdraw if pending approval or pending revision approval
    return (
      status === 'pending_approval' || 
      revisionStatus === 'pending_revision_approval'
    );
  }, [currentListing]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, []);

  return {
    currentListing,
    originalData,
    autoSaveStatus,
    fetchListing,
    getDisplayStatus,
    hasCriticalChanges,
    triggerAutoSave,
    saveDraft,
    submitForApproval,
    withdrawSubmission,
    canWithdraw,
    setCurrentListing
  };
};
